import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

import { cn } from '../lib/utils';
import { apiFetch } from '../lib/api';

const toHsl = (token: string, fallback: string) => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return value ? `hsl(${value})` : fallback;
};

type PlayerAward = { icon: string; title: string; description: string; season?: string };
type PlayerAwardWithPriority = PlayerAward & { placementPriority: number; type?: string };

const trophyByRank: Record<1 | 2 | 3, PlayerAward> = {
  1: { icon: '/static/img/inf-gold.svg', title: '1 место', description: 'Топ-1 игрок ротации' },
  2: { icon: '/static/img/inf-silver.svg', title: '2 место', description: 'Топ-2 игрок ротации' },
  3: { icon: '/static/img/inf-bronze.svg', title: '3 место', description: 'Топ-3 игрок ротации' },
};

const playerAwardsHistory: Record<string, PlayerAward[]> = {
  bordo: [
    { icon: '/static/img/star.svg', title: 'MVP ротации', description: 'Лучший игрок ротации #24', season: 'Ротация #24' },
    { icon: '/static/img/star.svg', title: 'Серебро ротации', description: '2 место в ротации #23', season: 'Ротация #23' },
  ],
  sova: [{ icon: '/static/img/star.svg', title: 'Бронза ротации', description: '3 место в ротации #24', season: 'Ротация #24' }],
};

const playerRanks: Record<string, number> = {
  bordo: 1,
  falcon: 2,
  ranger: 3,
};

const achievementIcons: Record<string, string> = {
  ks_ach: '/static/img/ks_ach_win.svg',
  med: '/static/img/med_win.svg',
  bspn: '/static/img/snp-01.svg',
  mspn: '/static/img/snp-02.svg',
  avi: '/static/img/avi-01.svg',
  omp: '/static/img/omp-01.svg',
  term: '/static/img/term.svg',
  veh_1: '/static/img/veh-gold.svg',
  veh_2: '/static/img/veh-silver.svg',
  veh_3: '/static/img/veh-bronze.svg',
  art_1: '/static/img/art-gold.svg',
  art_2: '/static/img/art-silver.svg',
  art_3: '/static/img/art-bronze.svg',
  inf_1: '/static/img/inf-gold.svg',
  inf_2: '/static/img/inf-silver.svg',
  inf_3: '/static/img/inf-bronze.svg',
  ks_1: '/static/img/ks-gold.svg',
  ks_2: '/static/img/ks-silver.svg',
  ks_3: '/static/img/ks-bronze.svg',
};

const achievementTitles: Record<string, string> = {
  med: 'Ангел-хранитель',
  bspn: 'Зоркий глаз',
  mspn: 'Меткий глаз',
  omp: 'ОМП',
  ks_ach: 'Блицкриг',
  avi: 'Авиатор',
  term: 'Terminator',
};

const veh1CupIcon = '/static/img/veh-diamond.svg';
const makeVeh1Cup = (season?: string): PlayerAwardWithPriority => ({
  icon: veh1CupIcon,
  title: '1 место',
  description: '5 побед в категории Техника',
  season,
  placementPriority: 0,
  type: 'veh_1_cup',
});

const replaceVeh1Awards = (awards: PlayerAwardWithPriority[]) => {
  const result: PlayerAwardWithPriority[] = [];
  let veh1Buffer: PlayerAwardWithPriority[] = [];

  awards.forEach((award) => {
    if (award.type === 'veh_1') {
      veh1Buffer.push(award);
      if (veh1Buffer.length === 5) {
        result.push(makeVeh1Cup(veh1Buffer[0]?.season));
        veh1Buffer = [];
      }
      return;
    }
    result.push(award);
  });

  if (veh1Buffer.length > 0) {
    result.push(...veh1Buffer);
  }

  return result;
};

function useChart(theme: 'light' | 'dark') {
  const instance = useRef<EChartsType | null>(null);
  const resizeHandler = useRef<(() => void) | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const [readyTick, setReadyTick] = useState(0);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserver.current) {
      resizeObserver.current.disconnect();
      resizeObserver.current = null;
    }
    if (resizeHandler.current) {
      window.removeEventListener('resize', resizeHandler.current);
      resizeHandler.current = null;
    }

    if (instance.current) {
      instance.current.dispose();
      instance.current = null;
    }

    if (!node) return;

    const initChart = () => {
      if (!node || instance.current) return;
      instance.current = echarts.init(node);
      resizeHandler.current = () => instance.current?.resize();
      window.addEventListener('resize', resizeHandler.current);
      setReadyTick((value) => value + 1);
    };

    if (node.clientWidth === 0 || node.clientHeight === 0) {
      resizeObserver.current = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          resizeObserver.current?.disconnect();
          resizeObserver.current = null;
          initChart();
        }
      });
      resizeObserver.current.observe(node);
      return;
    }

    initChart();
  }, [theme]);

  const styles = useMemo(() => {
    return {
      foreground: toHsl('--foreground', '#e5e7eb'),
      muted: toHsl('--muted-foreground', '#9ca3af'),
      border: toHsl('--border', '#2a2d31'),
      card: toHsl('--card', theme === 'dark' ? '#1a1d21' : '#ffffff'),
      positive: toHsl('--success', '#27ae60'),
      negative: toHsl('--destructive', '#eb5757'),
      accent: toHsl('--accent', '#ffde00'),
    };
  }, [theme]);

  const setOption = (option: echarts.EChartsOption) => {
    if (!instance.current) return;
    instance.current.setOption(option, true);
    instance.current.resize();
  };

  return { ref, setOption, styles, readyTick } as const;
}

export type PlayerProfile = {
  summarySeries: { mission: string; frags: number; tk: number }[];
  weaponFrags: { weapon: string; frags: number }[];
  playStyle: { axis: string; value: number }[];
  combatProfile: { axis: string; value: number }[];
  attendance: { mission: string; map: string; server: string; date: string }[];
  fragsLog: Array<{
    victim: string;
    victimSquad: string;
    weapon: string;
    distance: number;
    replay: string;
    cls: string;
  }>;
  tkLog: Array<{
    victim: string;
    victimSquad: string;
    weapon: string;
    distance: number;
    replay: string;
    cls: string;
  }>;
  deathsLog: Array<{
    killer: string;
    squad: string;
    tk: number;
    weapon: string;
    distance: number;
    replay: string;
    cls: string;
  }>;
};

type PlayerAchievement = { type: string; description: string };

type PlayerDetailResponse = PlayerProfile & {
  callsign: string;
  achievements?: PlayerAchievement[];
};

const defaultProfile: PlayerProfile = {
  summarySeries: [
    { mission: 'Игра 1', frags: 12, tk: 1 },
    { mission: 'Игра 2', frags: 9, tk: 0 },
    { mission: 'Игра 3', frags: 15, tk: 0 },
    { mission: 'Игра 4', frags: 8, tk: 2 },
  ],
  weaponFrags: [
    { weapon: 'АК-74М', frags: 15 },
    { weapon: 'М-16', frags: 13 },
    { weapon: 'СВД', frags: 3 },
  ],
  playStyle: [
    { axis: 'Агрессор', value: 30 },
    { axis: 'Выживальщик', value: 25 },
    { axis: 'Поддержка', value: 15 },
    { axis: 'Командный игрок', value: 20 },
    { axis: 'Хаотик', value: 10 },
  ],
  combatProfile: [
    { axis: 'Пехотинец', value: 35 },
    { axis: 'Командир', value: 20 },
    { axis: 'Экипажник', value: 25 },
    { axis: 'Артиллерист', value: 20 },
  ],
  attendance: [
    { mission: '196_Rch_Repair_zone_v6', map: 'Enoch', server: 'T3', date: '12-12-25 23:34:06' },
    { mission: '196_te_wybuchy_z_tylu_v10', map: 'chernarus', server: 'T3', date: '12-12-25 21:15:45' },
  ],
  fragsLog: [
    {
      victim: 'Troin',
      victimSquad: 'V',
      weapon: 'T-72B (obr. 1985g.)',
      distance: 332,
      replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
      cls: 'MBT',
    },
  ],
  tkLog: [
    {
      victim: 'Friendly',
      victimSquad: 'TEAM',
      weapon: 'AK-74M',
      distance: 45,
      replay: 'T3.2025-12-12-21-15-45.TSG@196_te_wybuchy_z_tylu_v10.chernarus',
      cls: 'Rifle',
    },
  ],
  deathsLog: [
    {
      killer: 'Asriel',
      squad: 'DELTA',
      tk: 0,
      weapon: 'SCAR-H (LB)',
      distance: 54,
      replay: 'T2.2025-12-12-23-39-28.TSG@196_Rch_Repair_zone_v6.Enoch',
      cls: 'Rifle',
    },
    {
      killer: 'Bazl',
      squad: '75th',
      tk: 0,
      weapon: 'MSBS-5,56K Grot C16 (HG)',
      distance: 26,
      replay: 'T2.2025-12-12-21-04-48.TSG@196_te_wybuchy_z_tylu_v10.chernarus',
      cls: '—',
    },
  ],
};

const playerProfiles: Record<string, PlayerProfile> = {
  bordo: {
    ...defaultProfile,
    summarySeries: [
      { mission: '196_Rch_Repair_zone_v6', frags: 18, tk: 1 },
      { mission: '196_te_wybuchy_z_tylu_v10', frags: 16, tk: 0 },
      { mission: '196_frost_fire', frags: 20, tk: 0 },
      { mission: '196_misty_ops', frags: 14, tk: 1 },
    ],
    playStyle: [
      { axis: 'Агрессор', value: 40 },
      { axis: 'Выживальщик', value: 15 },
      { axis: 'Поддержка', value: 20 },
      { axis: 'Командный игрок', value: 20 },
      { axis: 'Хаотик', value: 5 },
    ],
    combatProfile: [
      { axis: 'Пехотинец', value: 45 },
      { axis: 'Командир', value: 20 },
      { axis: 'Экипажник', value: 20 },
      { axis: 'Артиллерист', value: 15 },
    ],
    fragsLog: [
      {
        victim: 'Troin',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 332,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Kaskader',
        victimSquad: 'BW',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 609,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Wormhole',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 294,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Kennedy',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 292,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'TU_M1134',
        victimSquad: '-',
        weapon: 'AK-74M',
        distance: 284,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'Rifle',
      },
      {
        victim: 'German',
        victimSquad: 'MIR_c',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 592,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Lance',
        victimSquad: 'SSG',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 475,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Priest',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 578,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'ARTYXA',
        victimSquad: 'BW',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 540,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Askari',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 539,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'TU_1128_MGS',
        victimSquad: '-',
        weapon: 'AK-74M',
        distance: 618,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'Rifle',
      },
      {
        victim: 'TU_M1126_M2',
        victimSquad: '-',
        weapon: 'AK-74M',
        distance: 613,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'Rifle',
      },
      {
        victim: 'TU_M1126_M2',
        victimSquad: '-',
        weapon: 'AK-74M',
        distance: 635,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'Rifle',
      },
      {
        victim: 'Yar',
        victimSquad: 'JAGER',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 660,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Loken',
        victimSquad: 'V',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 481,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'Ksim',
        victimSquad: 'SR',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 852,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
      {
        victim: 'PLECEHb',
        victimSquad: 'SR',
        weapon: 'T-72B (obr. 1985g.)',
        distance: 686,
        replay: 'T3.2025-12-12-23-34-06.TSG@196_Rch_Repair_zone_v6.Enoch',
        cls: 'MBT',
      },
    ],
    tkLog: [
      {
        victim: 'FriendlyFire',
        victimSquad: 'TOR',
        weapon: 'AK-74M',
        distance: 28,
        replay: 'T3.2025-12-12-21-15-45.TSG@196_te_wybuchy_z_tylu_v10.chernarus',
        cls: 'Rifle',
      },
    ],
  },
  sova: {
    ...defaultProfile,
    summarySeries: [
      { mission: '196_Rch_Repair_zone_v6', frags: 14, tk: 0 },
      { mission: '196_te_wybuchy_z_tylu_v10', frags: 11, tk: 1 },
      { mission: '196_frost_fire', frags: 12, tk: 0 },
    ],
    playStyle: [
      { axis: 'Агрессор', value: 20 },
      { axis: 'Выживальщик', value: 30 },
      { axis: 'Поддержка', value: 15 },
      { axis: 'Командный игрок', value: 25 },
      { axis: 'Хаотик', value: 10 },
    ],
    combatProfile: [
      { axis: 'Пехотинец', value: 25 },
      { axis: 'Командир', value: 30 },
      { axis: 'Экипажник', value: 25 },
      { axis: 'Артиллерист', value: 20 },
    ],
    weaponFrags: [
      { weapon: 'АК-74М', frags: 10 },
      { weapon: 'М-16', frags: 8 },
      { weapon: 'СВД', frags: 6 },
    ],
  },
};

const playStyleHints: Record<string, string> = {
  Агрессор: 'Идёт вперёд и берёт инициативу: первым выходит на контакт, активно ищет цели.',
  Выживальщик: 'Минимизирует риски, бережёт ресурсы и остаётся в строю как можно дольше.',
  Поддержка: 'Работает на усиление команды: лечение, пополнение боекомплекта, укрепления.',
  'Командный игрок': 'Соблюдает план, держит строй и помогает выполнять задачу отряда.',
  Хаотик: 'Действует нестандартно и непредсказуемо, создавая противнику неудобства.',
};

const combatProfileHints: Record<string, string> = {
  Пехотинец: 'Основной вклад в пехотных столкновениях и ближних контактах.',
  Командир: 'Координация группы, ведение боя и распределение целей.',
  Экипажник: 'Эффективность в технике и поддержка боевых машин.',
  Артиллерист: 'Работа с артиллерией и поддержка огнём на дистанции.',
};

export function PlayerDetailPage({
  callsign,
  theme,
  onBack,
}: {
  callsign: string;
  theme: 'light' | 'dark';
  onBack: () => void;
}): JSX.Element {
  const { data: playerDetail } = useQuery({
    queryKey: ['player-detail', callsign],
    queryFn: () => apiFetch<PlayerDetailResponse>(`/player-detail/${encodeURIComponent(callsign)}/`),
    enabled: Boolean(callsign),
  });
  const [tab, setTab] = useState<'summary' | 'attendance' | 'frags' | 'tk' | 'deaths'>('summary');
  const normalized = callsign.toLowerCase();
  const fallbackProfile = playerProfiles[normalized] ?? defaultProfile;
  const resolveSeries = <T,>(value: T[] | undefined, fallback: T[]) =>
    value && value.length > 0 ? value : fallback;
  const profile = useMemo(
    () => ({
      summarySeries: playerDetail?.summarySeries ?? fallbackProfile.summarySeries,
      weaponFrags: playerDetail?.weaponFrags ?? fallbackProfile.weaponFrags,
      playStyle: resolveSeries(playerDetail?.playStyle, fallbackProfile.playStyle),
      combatProfile: resolveSeries(playerDetail?.combatProfile, fallbackProfile.combatProfile),
      attendance: playerDetail?.attendance ?? fallbackProfile.attendance,
      fragsLog: playerDetail?.fragsLog ?? fallbackProfile.fragsLog,
      tkLog: playerDetail?.tkLog ?? fallbackProfile.tkLog,
      deathsLog: playerDetail?.deathsLog ?? fallbackProfile.deathsLog,
    }),
    [fallbackProfile, playerDetail]
  );

  const trophies = useMemo<PlayerAwardWithPriority[]>(() => {
    const placementIcons: Record<number, string> = {
      1: '/static/img/inf-gold.svg',
      2: '/static/img/inf-silver.svg',
      3: '/static/img/inf-bronze.svg',
    };
    const extractSeason = (description: string) => {
      const match = description.match(/Ротация:\s*([^;]+)/);
      return match?.[1]?.trim();
    };

    const apiAwards =
      playerDetail?.achievements?.map((achievement) => {
        const placementMatch = achievement.type.match(/_(1|2|3)$/);
        const placement = placementMatch ? Number(placementMatch[1]) : null;
        const icon =
          achievementIcons[achievement.type] ??
          (placement ? placementIcons[placement] : '/static/img/star.svg');
        return {
          type: achievement.type,
          icon,
          title: placement ? `${placement} место` : achievementTitles[achievement.type] ?? achievement.type,
          description: achievement.description,
          season: extractSeason(achievement.description),
          placementPriority: placement ?? 4,
        };
      }) ?? [];

    if (apiAwards.length > 0) {
      return replaceVeh1Awards(apiAwards);
    }

    const rank = playerRanks[normalized];
    const current =
      rank && rank <= 3
        ? [{ ...trophyByRank[rank as 1 | 2 | 3], season: 'Текущая ротация', placementPriority: rank }]
        : [];
    const history = playerAwardsHistory[normalized] ?? [];
    return [...current, ...history].map((award) => ({ ...award, placementPriority: 4 }));
  }, [normalized, playerDetail?.achievements]);

  const summaryChart = useChart(theme);
  const weaponsChart = useChart(theme);
  const playStyleChart = useChart(theme);
  const combatProfileChart = useChart(theme);

  useEffect(() => {
    if (!profile.summarySeries.length) return;
    const { styles } = summaryChart;
    const summarySeries = [...profile.summarySeries].reverse();
    summaryChart.setOption({
      grid: { left: 52, right: 16, top: 32, bottom: 48 },
      legend: { data: ['Фраги', 'ТК'], textStyle: { color: styles.muted } },
      xAxis: {
        type: 'category',
        data: summarySeries.map((row) => row.mission),
        axisLine: { lineStyle: { color: styles.border } },
        axisLabel: { show: false, color: styles.muted },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: styles.border } },
        splitLine: { lineStyle: { color: styles.border, type: 'dashed' } },
        axisLabel: { color: styles.muted },
      },
      series: [
        {
          name: 'Фраги',
          type: 'bar',
          data: summarySeries.map((row) => row.frags),
          itemStyle: { color: styles.positive },
          emphasis: { focus: 'series' },
          barWidth: 18,
        },
        {
          name: 'ТК',
          type: 'bar',
          data: summarySeries.map((row) => row.tk),
          itemStyle: { color: styles.negative },
          emphasis: { focus: 'series' },
          barWidth: 14,
        },
      ],
      tooltip: { trigger: 'axis', backgroundColor: styles.card, borderColor: styles.border, textStyle: { color: styles.foreground } },
      animationDuration: 0,
    });
  }, [profile.summarySeries, summaryChart, theme, summaryChart.readyTick]);

  useEffect(() => {
    if (!profile.weaponFrags.length) return;
    const { styles } = weaponsChart;
    weaponsChart.setOption({
      grid: { left: 80, right: 32, top: 24, bottom: 32 },
      xAxis: { type: 'value', axisLine: { lineStyle: { color: styles.border } }, splitLine: { lineStyle: { color: styles.border, type: 'dashed' } }, axisLabel: { color: styles.muted } },
      yAxis: { type: 'category', data: profile.weaponFrags.map((row) => row.weapon), axisLine: { lineStyle: { color: styles.border } }, axisLabel: { color: styles.muted } },
      series: [
        {
          type: 'bar',
          data: profile.weaponFrags.map((row) => row.frags),
          itemStyle: { color: styles.accent },
          barWidth: 18,
          large: true,
          emphasis: { focus: 'none', disabled: true },
          animation: false,
        },
      ],
      tooltip: {
        trigger: 'axis',
        backgroundColor: styles.card,
        borderColor: styles.border,
        textStyle: { color: styles.foreground },
        confine: true,
        axisPointer: {
          type: 'shadow',
          snap: true,
          animation: false,
          label: { show: false },
        },
      },
      animationDuration: 0,
      animationDurationUpdate: 0,
      animation: false,
    });
  }, [profile.weaponFrags, weaponsChart, theme, weaponsChart.readyTick]);

  useEffect(() => {
    if (!profile.playStyle.length) return;
    const { styles } = playStyleChart;

    const accentHsl = styles.accent.replace('hsl(', 'hsla(').replace(')', ', 0.18)');
    const strokeAccent = styles.accent;

    playStyleChart.setOption({
      tooltip: {
        trigger: 'item',
        backgroundColor: styles.card,
        borderColor: styles.border,
        textStyle: { color: styles.foreground },
        formatter: (params: any) => {
          const values = params?.value as number[] | undefined;
          if (!values) return '';
          return profile.playStyle
            .map((item, index) => `${item.axis}: ${values[index] ?? 0}`)
            .join('<br/>');
        },
      },
      radar: {
        radius: '65%',
        splitNumber: 5,
        axisName: { color: styles.foreground, fontSize: 12 },
        splitLine: { lineStyle: { color: styles.border } },
        splitArea: { areaStyle: { color: ['transparent'] } },
        axisLine: { lineStyle: { color: styles.border } },
        indicator: profile.playStyle.map((item) => ({ name: item.axis, max: 100 })),
      },
      series: [
        {
          type: 'radar',
          name: 'Стиль игры',
          data: [
            {
              value: profile.playStyle.map((item) => item.value),
              areaStyle: { color: accentHsl },
              lineStyle: { color: strokeAccent, width: 2 },
              itemStyle: { color: strokeAccent },
            },
          ],
          emphasis: { focus: 'none', disabled: true },
          animationDuration: 0,
          animationDurationUpdate: 0,
          animation: false,
        },
      ],
      animationDuration: 0,
      animationDurationUpdate: 0,
      animation: false,
    });
  }, [playStyleChart, profile.playStyle, theme, playStyleChart.readyTick]);

  useEffect(() => {
    if (!profile.combatProfile.length) return;
    const { styles } = combatProfileChart;

    const accentHsl = styles.accent.replace('hsl(', 'hsla(').replace(')', ', 0.18)');
    const strokeAccent = styles.accent;

    combatProfileChart.setOption({
      tooltip: {
        trigger: 'item',
        backgroundColor: styles.card,
        borderColor: styles.border,
        textStyle: { color: styles.foreground },
        formatter: (params: any) => {
          const values = params?.value as number[] | undefined;
          if (!values) return '';
          return profile.combatProfile
            .map((item, index) => `${item.axis}: ${values[index] ?? 0}`)
            .join('<br/>');
        },
      },
      radar: {
        radius: '65%',
        splitNumber: 4,
        axisName: { color: styles.foreground, fontSize: 12 },
        splitLine: { lineStyle: { color: styles.border } },
        splitArea: { areaStyle: { color: ['transparent'] } },
        axisLine: { lineStyle: { color: styles.border } },
        indicator: profile.combatProfile.map((item) => ({ name: item.axis, max: 100 })),
      },
      series: [
        {
          type: 'radar',
          name: 'Боевой профиль',
          data: [
            {
              value: profile.combatProfile.map((item) => item.value),
              areaStyle: { color: accentHsl },
              lineStyle: { color: strokeAccent, width: 2 },
              itemStyle: { color: strokeAccent },
            },
          ],
          emphasis: { focus: 'none', disabled: true },
          animationDuration: 0,
          animationDurationUpdate: 0,
          animation: false,
        },
      ],
      animationDuration: 0,
      animationDurationUpdate: 0,
      animation: false,
    });
  }, [combatProfileChart, profile.combatProfile, theme, combatProfileChart.readyTick]);

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: 'summary', label: 'Сводка' },
    { key: 'attendance', label: 'Посещаемость' },
    { key: 'frags', label: 'Фраги' },
    { key: 'tk', label: 'ТК' },
    { key: 'deaths', label: 'Смерти' },
  ];
  const rankedTrophies = trophies.filter((award) => award.placementPriority <= 3);
  const extraTrophies = trophies.filter((award) => award.placementPriority > 3);

  const renderReplayLink = (replay: string) => {
    const href = `https://replay.tsgames.ru/replay.php?file=${encodeURIComponent(replay)}&archive=1`;
    return (
      <a
        href={href}
        className="text-primary underline-offset-2 hover:underline"
        target="_blank"
        rel="noreferrer"
      >
        {replay}
      </a>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Персональная карточка игрока</p>
          <h2 className="text-3xl font-bold">{callsign}</h2>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary"
        >
          Назад
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={cn(
              'rounded-md border px-3 py-2 text-sm font-semibold transition',
              tab === item.key
                ? 'border-primary bg-primary/20 text-primary'
                : 'border-border bg-card text-foreground hover:border-primary/60'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === 'summary' && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-6"
          >
            {trophies.length > 0 && (
              <div className="card space-y-4 p-4">
                <div>
                  <p className="text-sm text-muted-foreground">Награды игрока</p>
                  <p className="text-2xl font-semibold">Трофеи</p>
                </div>
                <div className="space-y-4">
                  {rankedTrophies.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-3">
                      {rankedTrophies
                        .sort((left, right) => left.placementPriority - right.placementPriority)
                        .map((award, index) => (
                          <div
                            key={`${award.title}-${award.season ?? index}`}
                            className="flex h-full w-32 flex-col items-center gap-3 rounded-lg border border-border bg-card/60 py-3 text-center"
                          >
                            <div className="space-y-1 text-sm">
                              <p className="font-semibold">{award.title}</p>
                              {award.season && <p className="text-xs text-muted-foreground">{award.season}</p>}
                            </div>
                            <div className="flex items-center justify-center">
                              <img src={award.icon} alt={award.title} title={award.description} className="h-32 w-32" />
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  {extraTrophies.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-3">
                      {extraTrophies.map((award, index) => (
                        <div
                          key={`${award.title}-${award.season ?? index}-extra`}
                          className="flex h-full w-32 flex-col items-center gap-3 rounded-lg border border-border bg-card/60 py-3 text-center"
                        >
                          <div className="space-y-1 text-sm">
                            <p className="font-semibold">{award.title}</p>
                            {award.season && <p className="text-xs text-muted-foreground">{award.season}</p>}
                          </div>
                          <div className="flex items-center justify-center">
                            <img src={award.icon} alt={award.title} title={award.description} className="h-32 w-32" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/*
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Баланс ролей в стиле игры</p>
                <p className="text-2xl font-semibold">Профиль игрока</p>
              </div>
              <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
                <div className="space-y-3 rounded-lg border border-border bg-card/60 p-4">
                  <p className="text-sm font-semibold text-foreground">Поведенческий профиль</p>
                  <div ref={playStyleChart.ref} className="h-[300px] w-full" />
                </div>
                <div className="space-y-3 rounded-lg border border-border bg-card/60 p-4">
                  <p className="text-sm font-semibold text-foreground">Боевой профиль</p>
                  <div ref={combatProfileChart.ref} className="h-[300px] w-full" />
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2 rounded-lg border border-border bg-card/60 p-4 text-sm leading-relaxed">
                  <p className="font-semibold text-foreground">Поведенческий профиль</p>
                  <p className="text-muted-foreground">
                    Пять граней описывают стиль игры. Чем выше значение по оси, тем сильнее проявлен этот аспект. Проценты
                    суммированы до 100%.
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    {profile.playStyle.map((item) => (
                      <li key={item.axis} className="flex gap-2">
                        <span className="font-semibold text-foreground">{item.axis}:</span>
                        <span>{playStyleHints[item.axis] ?? 'Характеристика стилевого поведения.'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-2 rounded-lg border border-border bg-card/60 p-4 text-sm leading-relaxed">
                  <p className="font-semibold text-foreground">Боевой профиль</p>
                  <p className="text-muted-foreground">
                    Четыре оси показывают вклад игрока в ключевых боевых ролях. Чем выше значение, тем значимее вклад.
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    {profile.combatProfile.map((item) => (
                      <li key={item.axis} className="flex gap-2">
                        <span className="font-semibold text-foreground">{item.axis}:</span>
                        <span>{combatProfileHints[item.axis] ?? 'Характеристика боевой роли.'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            */}

            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Фраги и тимкиллы по миссиям</p>
                <p className="text-2xl font-semibold">Сводка</p>
              </div>
              <div ref={summaryChart.ref} className="h-[360px]" />
            </div>

            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Распределение фрагов по оружию</p>
                <p className="text-2xl font-semibold">Фраги / Оружие</p>
              </div>
              <div ref={weaponsChart.ref} className="h-[320px]" />
            </div>
          </motion.div>
        )}

        {tab === 'attendance' && (
          <motion.div
            key="attendance"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">
                  В этой таблице представлены все игры, на которых система зафиксировала ваше присутствие. Кол-во записей в таблице = вашей посещаемости.
                </p>
                <p className="text-2xl font-semibold">Посещаемость</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {['№', 'Миссия', 'Карта', 'Сервер', 'Дата и время'].map((col) => (
                        <th key={col} className="px-3 py-3 text-center align-middle font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profile.attendance.map((row, idx) => (
                      <tr
                        key={`${row.mission}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">{idx + 1}</td>
                        <td className="px-3 py-3 text-center align-middle">{renderReplayLink(row.mission)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.map}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.server}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'frags' && (
          <motion.div
            key="frags"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">
                  В этой таблице представлены все ваши фраги (вне зависимости от класса оружия), зафиксированные системой.
                </p>
                <p className="text-2xl font-semibold">Фраги</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {['№', 'Жертва', 'Отряд жертвы', 'Оружие', 'Дистанция, м', 'Реплей', 'Класс'].map((col) => (
                        <th key={col} className="px-3 py-3 text-center align-middle font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profile.fragsLog.map((row, idx) => (
                      <tr
                        key={`${row.victim}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">{idx + 1}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.victim}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.victimSquad}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.weapon}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.distance}</td>
                        <td className="px-3 py-3 text-center align-middle">{renderReplayLink(row.replay)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.cls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'tk' && (
          <motion.div
            key="tk"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Все зафиксированные тимкиллы игрока.</p>
                <p className="text-2xl font-semibold">ТК</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {['№', 'Жертва', 'Отряд жертвы', 'Оружие', 'Дистанция, м', 'Реплей', 'Класс'].map((col) => (
                        <th key={col} className="px-3 py-3 text-center align-middle font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profile.tkLog.map((row, idx) => (
                      <tr
                        key={`${row.victim}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">{idx + 1}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.victim}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.victimSquad}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.weapon}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.distance}</td>
                        <td className="px-3 py-3 text-center align-middle">{renderReplayLink(row.replay)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.cls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'deaths' && (
          <motion.div
            key="deaths"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">
                  В этой таблице представлены все ваши смерти, зафиксированные системой. В том числе "самоубийства" и смерти из неизвестного источника.
                </p>
                <p className="text-2xl font-semibold">Смерти</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {['№', 'Киллер', 'Отряд киллера', 'TK', 'Оружие', 'Дистанция, м', 'Реплей', 'Класс'].map((col) => (
                        <th key={col} className="px-3 py-3 text-center align-middle font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profile.deathsLog.map((row, idx) => (
                      <tr
                        key={`${row.killer}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">{idx + 1}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.killer}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.squad}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.tk}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.weapon}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.distance}</td>
                        <td className="px-3 py-3 text-center align-middle">{renderReplayLink(row.replay)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.cls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
